/**
 * Offline Sync Service
 *
 * Provides offline-first functionality with:
 * - Sync queue management with IndexedDB persistence
 * - Online/offline status detection
 * - Automatic sync when back online
 * - Conflict resolution strategies
 */

import { logger } from '../lib/logger'

// ============ Types ============

/** Operation types that can be queued */
export type OperationType =
  | 'clock_in'
  | 'clock_out'
  | 'start_break'
  | 'end_break'
  | 'update_session'

/** Sync operation status */
export type SyncStatus = 'pending' | 'syncing' | 'completed' | 'failed' | 'conflict'

/** Conflict resolution strategy */
export type ConflictResolution = 'client_wins' | 'server_wins' | 'merge' | 'manual'

// ============ Operation Data Types ============

/** Data for clock-in operation */
export interface ClockInOperationData {
  userId: string
  siteId?: string
  location?: { lat: number; lng: number; accuracy: number }
  timestamp: number
  deviceInfo?: string
}

/** Data for clock-out operation */
export interface ClockOutOperationData {
  sessionId: string
  userId: string
  location?: { lat: number; lng: number; accuracy: number }
  timestamp: number
  dailyReport?: string
}

/** Data for start-break operation */
export interface StartBreakOperationData {
  sessionId: string
  userId: string
  timestamp: number
  location?: { lat: number; lng: number }
}

/** Data for end-break operation */
export interface EndBreakOperationData {
  sessionId: string
  userId: string
  breakId: string
  timestamp: number
  location?: { lat: number; lng: number }
}

/** Data for update-session operation */
export interface UpdateSessionOperationData {
  sessionId: string
  updates: {
    notes?: string
    status?: string
    [key: string]: unknown
  }
}

/** Union type for all operation data */
export type OperationData =
  | ClockInOperationData
  | ClockOutOperationData
  | StartBreakOperationData
  | EndBreakOperationData
  | UpdateSessionOperationData

/** A queued operation for offline sync */
export interface QueuedOperation<T = OperationData> {
  id: string
  type: OperationType
  data: T
  timestamp: number
  status: SyncStatus
  retryCount: number
  maxRetries: number
  lastError?: string
  conflictData?: T
  userId: string
  /** Client-generated ID for optimistic updates */
  clientId?: string
  /** Server-assigned ID after sync */
  serverId?: string
}

/** Sync queue state */
export interface SyncQueueState {
  operations: QueuedOperation[]
  lastSyncTime: number | null
  isSyncing: boolean
  syncError: string | null
}

/** Listener callback type */
type SyncQueueListener = (state: SyncQueueState) => void

/** Operation handler function type */
type OperationHandler<T = Record<string, unknown>> = (
  operation: QueuedOperation<T>
) => Promise<{ serverId?: string; success: boolean; error?: string }>

// ============ IndexedDB Manager ============

const DB_NAME = 'petru-offline-db'
const DB_VERSION = 1
const OPERATIONS_STORE = 'sync_operations'
const METADATA_STORE = 'sync_metadata'

class IndexedDBManager {
  private db: IDBDatabase | null = null
  private dbPromise: Promise<IDBDatabase> | null = null

  async open(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db
    }

    if (this.dbPromise) {
      return this.dbPromise
    }

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        logger.error('Failed to open IndexedDB', request.error as Error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(request.result)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Operations store
        if (!db.objectStoreNames.contains(OPERATIONS_STORE)) {
          const store = db.createObjectStore(OPERATIONS_STORE, { keyPath: 'id' })
          store.createIndex('status', 'status', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
          store.createIndex('userId', 'userId', { unique: false })
          store.createIndex('type', 'type', { unique: false })
        }

        // Metadata store
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          db.createObjectStore(METADATA_STORE, { keyPath: 'key' })
        }
      }
    })

    return this.dbPromise
  }

  async addOperation<T>(operation: QueuedOperation<T>): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readwrite')
      const store = tx.objectStore(OPERATIONS_STORE)
      const request = store.add(operation)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async updateOperation<T>(operation: QueuedOperation<T>): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readwrite')
      const store = tx.objectStore(OPERATIONS_STORE)
      const request = store.put(operation)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async deleteOperation(id: string): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readwrite')
      const store = tx.objectStore(OPERATIONS_STORE)
      const request = store.delete(id)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async getOperation<T>(id: string): Promise<QueuedOperation<T> | undefined> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readonly')
      const store = tx.objectStore(OPERATIONS_STORE)
      const request = store.get(id)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result as QueuedOperation<T> | undefined)
    })
  }

  async getAllOperations<T>(): Promise<QueuedOperation<T>[]> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readonly')
      const store = tx.objectStore(OPERATIONS_STORE)
      const request = store.getAll()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result as QueuedOperation<T>[])
    })
  }

  async getPendingOperations<T>(): Promise<QueuedOperation<T>[]> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readonly')
      const store = tx.objectStore(OPERATIONS_STORE)
      const index = store.index('status')
      const request = index.getAll('pending')

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        // Also get failed operations that can be retried
        const pendingOps = request.result as QueuedOperation<T>[]
        const failedRequest = index.getAll('failed')

        failedRequest.onerror = () => reject(failedRequest.error)
        failedRequest.onsuccess = () => {
          const failedOps = (failedRequest.result as QueuedOperation<T>[])
            .filter(op => op.retryCount < op.maxRetries)
          resolve([...pendingOps, ...failedOps].sort((a, b) => a.timestamp - b.timestamp))
        }
      }
    })
  }

  async getOperationsByUser<T>(userId: string): Promise<QueuedOperation<T>[]> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readonly')
      const store = tx.objectStore(OPERATIONS_STORE)
      const index = store.index('userId')
      const request = index.getAll(userId)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result as QueuedOperation<T>[])
    })
  }

  async setMetadata(key: string, value: unknown): Promise<void> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(METADATA_STORE, 'readwrite')
      const store = tx.objectStore(METADATA_STORE)
      const request = store.put({ key, value })

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async getMetadata<T>(key: string): Promise<T | undefined> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(METADATA_STORE, 'readonly')
      const store = tx.objectStore(METADATA_STORE)
      const request = store.get(key)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const result = request.result as { key: string; value: T } | undefined
        resolve(result?.value)
      }
    })
  }

  async clearCompletedOperations(): Promise<number> {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(OPERATIONS_STORE, 'readwrite')
      const store = tx.objectStore(OPERATIONS_STORE)
      const index = store.index('status')
      const request = index.openCursor('completed')
      let deletedCount = 0

      request.onerror = () => reject(request.error)
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result
        if (cursor) {
          cursor.delete()
          deletedCount++
          cursor.continue()
        } else {
          resolve(deletedCount)
        }
      }
    })
  }
}

// ============ Sync Queue Manager ============

class SyncQueueManager {
  private dbManager: IndexedDBManager
  private listeners: Set<SyncQueueListener> = new Set()
  private handlers: Map<OperationType, OperationHandler> = new Map()
  private state: SyncQueueState = {
    operations: [],
    lastSyncTime: null,
    isSyncing: false,
    syncError: null
  }
  private syncInProgress = false
  private initialized = false

  constructor() {
    this.dbManager = new IndexedDBManager()
    this.setupOnlineListener()
  }

  private setupOnlineListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        logger.info('Network connection restored, triggering sync')
        void this.sync()
      })
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized) return

    try {
      await this.dbManager.open()
      const operations = await this.dbManager.getAllOperations()
      const lastSyncTime = await this.dbManager.getMetadata<number>('lastSyncTime')

      this.state = {
        ...this.state,
        operations,
        lastSyncTime: lastSyncTime ?? null
      }

      this.initialized = true
      this.notifyListeners()

      // Auto-sync pending operations if online
      if (navigator.onLine) {
        void this.sync()
      }
    } catch (error) {
      logger.error('Failed to initialize sync queue', error as Error)
      throw error
    }
  }

  /**
   * Register a handler for an operation type
   */
  registerHandler<T>(type: OperationType, handler: OperationHandler<T>): void {
    this.handlers.set(type, handler as OperationHandler)
  }

  /**
   * Add an operation to the queue
   */
  async enqueue<T extends Record<string, unknown>>(
    type: OperationType,
    data: T,
    userId: string,
    options: { clientId?: string; maxRetries?: number } = {}
  ): Promise<QueuedOperation<T>> {
    const operation: QueuedOperation<T> = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      maxRetries: options.maxRetries ?? 3,
      userId,
      clientId: options.clientId
    }

    await this.dbManager.addOperation(operation)
    this.state.operations.push(operation as QueuedOperation)
    this.notifyListeners()

    // If online, trigger immediate sync
    if (navigator.onLine && !this.syncInProgress) {
      void this.sync()
    }

    return operation
  }

  /**
   * Sync all pending operations
   */
  async sync(): Promise<{ synced: number; failed: number; conflicts: number }> {
    if (this.syncInProgress || !navigator.onLine) {
      return { synced: 0, failed: 0, conflicts: 0 }
    }

    this.syncInProgress = true
    this.state.isSyncing = true
    this.state.syncError = null
    this.notifyListeners()

    let synced = 0
    let failed = 0
    let conflicts = 0

    try {
      const pendingOperations = await this.dbManager.getPendingOperations()

      for (const operation of pendingOperations) {
        const handler = this.handlers.get(operation.type)

        if (!handler) {
          logger.warn(`No handler registered for operation type: ${operation.type}`)
          continue
        }

        // Update status to syncing
        operation.status = 'syncing'
        await this.dbManager.updateOperation(operation)
        this.updateOperationInState(operation)

        try {
          const result = await handler(operation)

          if (result.success) {
            operation.status = 'completed'
            operation.serverId = result.serverId
            synced++
          } else if (result.error?.includes('conflict')) {
            operation.status = 'conflict'
            operation.lastError = result.error
            conflicts++
          } else {
            operation.status = 'failed'
            operation.retryCount++
            operation.lastError = result.error
            failed++
          }
        } catch (error) {
          operation.status = 'failed'
          operation.retryCount++
          operation.lastError = error instanceof Error ? error.message : 'Unknown error'
          failed++
        }

        await this.dbManager.updateOperation(operation)
        this.updateOperationInState(operation)
      }

      // Update last sync time
      this.state.lastSyncTime = Date.now()
      await this.dbManager.setMetadata('lastSyncTime', this.state.lastSyncTime)

      // Clean up completed operations
      await this.dbManager.clearCompletedOperations()
      this.state.operations = this.state.operations.filter(op => op.status !== 'completed')

      logger.info('Sync completed', { synced, failed, conflicts })
    } catch (error) {
      this.state.syncError = error instanceof Error ? error.message : 'Sync failed'
      logger.error('Sync failed', error as Error)
    } finally {
      this.syncInProgress = false
      this.state.isSyncing = false
      this.notifyListeners()
    }

    return { synced, failed, conflicts }
  }

  /**
   * Retry a failed operation
   */
  async retryOperation(operationId: string): Promise<boolean> {
    const operation = await this.dbManager.getOperation(operationId)

    if (!operation) {
      logger.warn(`Operation not found: ${operationId}`)
      return false
    }

    if (operation.status !== 'failed' && operation.status !== 'conflict') {
      logger.warn(`Operation is not in a retryable state: ${operation.status}`)
      return false
    }

    operation.status = 'pending'
    operation.retryCount = 0
    await this.dbManager.updateOperation(operation)
    this.updateOperationInState(operation)

    if (navigator.onLine) {
      void this.sync()
    }

    return true
  }

  /**
   * Resolve a conflict with a specified strategy
   */
  async resolveConflict<T extends Record<string, unknown>>(
    operationId: string,
    resolution: ConflictResolution,
    mergedData?: T
  ): Promise<boolean> {
    const operation = await this.dbManager.getOperation<T>(operationId)

    if (!operation || operation.status !== 'conflict') {
      return false
    }

    switch (resolution) {
      case 'client_wins':
        // Re-queue with the client data
        operation.status = 'pending'
        operation.retryCount = 0
        break

      case 'server_wins':
        // Mark as completed, discard client changes
        operation.status = 'completed'
        break

      case 'merge':
        if (!mergedData) {
          logger.error('Merged data required for merge resolution')
          return false
        }
        operation.data = mergedData
        operation.status = 'pending'
        operation.retryCount = 0
        break

      case 'manual':
        // Mark as completed, let user handle manually
        operation.status = 'completed'
        break
    }

    await this.dbManager.updateOperation(operation)
    this.updateOperationInState(operation as QueuedOperation)

    if (operation.status === 'pending' && navigator.onLine) {
      void this.sync()
    }

    return true
  }

  /**
   * Cancel a pending operation
   */
  async cancelOperation(operationId: string): Promise<boolean> {
    const operation = await this.dbManager.getOperation(operationId)

    if (!operation || operation.status === 'syncing') {
      return false
    }

    await this.dbManager.deleteOperation(operationId)
    this.state.operations = this.state.operations.filter(op => op.id !== operationId)
    this.notifyListeners()

    return true
  }

  /**
   * Get pending operation count
   */
  getPendingCount(): number {
    return this.state.operations.filter(
      op => op.status === 'pending' || op.status === 'failed'
    ).length
  }

  /**
   * Get conflict count
   */
  getConflictCount(): number {
    return this.state.operations.filter(op => op.status === 'conflict').length
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: SyncQueueListener): () => void {
    this.listeners.add(listener)
    // Immediately notify with current state
    listener(this.state)
    return () => this.listeners.delete(listener)
  }

  /**
   * Get current state
   */
  getState(): SyncQueueState {
    return { ...this.state }
  }

  /**
   * Get operations by user
   */
  async getOperationsByUser(userId: string): Promise<QueuedOperation[]> {
    return this.dbManager.getOperationsByUser(userId)
  }

  private updateOperationInState(operation: QueuedOperation): void {
    const index = this.state.operations.findIndex(op => op.id === operation.id)
    if (index !== -1) {
      this.state.operations[index] = operation
    }
    this.notifyListeners()
  }

  private notifyListeners(): void {
    const stateCopy = { ...this.state, operations: [...this.state.operations] }
    this.listeners.forEach(listener => listener(stateCopy))
  }
}

// ============ Singleton Instance ============

export const syncQueue = new SyncQueueManager()

// ============ Convenience Functions ============

/**
 * Initialize the sync queue (call on app startup)
 */
export async function initializeSyncQueue(): Promise<void> {
  await syncQueue.initialize()
}

/**
 * Enqueue an operation for offline sync
 */
export async function enqueueOperation<T extends Record<string, unknown>>(
  type: OperationType,
  data: T,
  userId: string,
  options?: { clientId?: string; maxRetries?: number }
): Promise<QueuedOperation<T>> {
  return syncQueue.enqueue(type, data, userId, options)
}

/**
 * Register a sync handler for an operation type
 */
export function registerSyncHandler<T>(
  type: OperationType,
  handler: OperationHandler<T>
): void {
  syncQueue.registerHandler(type, handler)
}

/**
 * Trigger a manual sync
 */
export async function triggerSync(): Promise<{ synced: number; failed: number; conflicts: number }> {
  return syncQueue.sync()
}

/**
 * Get the current sync queue state
 */
export function getSyncQueueState(): SyncQueueState {
  return syncQueue.getState()
}

/**
 * Subscribe to sync queue state changes
 */
export function subscribeSyncQueue(listener: SyncQueueListener): () => void {
  return syncQueue.subscribe(listener)
}

export default syncQueue
